import { Accessibles, Grants } from './types';

export interface UserSession {
  grants: Grants;
  accessibles: Accessibles;
  panel: {
    id: number;
  };
  user: {
    id: string;
    email: string;
    ownerID: string;
    sub: number;
    firstName: string;
    lastName: string;
    preferredLocal?: string;
    isConsumer: boolean;
  };
  organisation: {
    id: number;
    ownerID: string;
  };
  hero: {
    isImpersonating: boolean;
    isSupportUser: boolean;
  };
}

const SESSION_STORAGE_KEY = 'SamKnowsConsumerUser';

export class InvalidUser extends Error {
  constructor(message) {
    super(message);
    this.name = 'InvalidUser';
  }
}

export async function fetchAndSaveUserSession(): Promise<void> {
  const response = await fetch(
    '/namespace/samknows-api/integration/auth/session'
  );
  if (!response.ok) {
    throw response;
  }
  const session: UserSession = await response.json();

  if (!session.user.isConsumer) {
    throw new InvalidUser('User is not a consumer');
  }

  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

export function removeUserSession(): void {
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

function getUserSession(): UserSession {
  const storedSession = localStorage.getItem(SESSION_STORAGE_KEY);
  return JSON.parse(storedSession);
}

export function getUserHashedId(): string {
  const userSession = getUserSession();

  return userSession?.user?.id;
}

export function getUserSub(): number {
  const userSession = getUserSession();

  return userSession?.user?.sub;
}

export function getUserGrants(): Grants {
  const userSession = getUserSession();

  return userSession?.grants;
}

export function getUserAccessibles(): Accessibles {
  const userSession = getUserSession();

  return userSession?.accessibles;
}

export function getUserPreferredLocale(): string {
  const userSession = getUserSession();
  return userSession?.user?.preferredLocal?.replace('_', '-') || 'en_GB';
}

export function getUserOrganisationOwnerId(): string {
  const userSession = getUserSession();

  return userSession?.organisation?.ownerID;
}

/**
 * Check whether the user session has been setup
 *
 * We consider the user setup when the user session cookie is available
 *
 * The session is required for the app to work properly
 *
 * @returns {boolean}
 */
export function isUserSessionSetup(): boolean {
  return !!localStorage.getItem(SESSION_STORAGE_KEY);
}

/**
 * Check whether the user is being being remotely controlled from TE Hero
 *
 * This happens when:
 * - user is impersonated: login as a user from Hero
 * - user is a generic support user: login at organisation level from Hero
 *
 * @returns {boolean}
 */
export function isUserRemotelyControlled(): boolean {
  const userSession = getUserSession();
  return userSession?.hero?.isImpersonating || userSession?.hero?.isSupportUser;
}
